import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useI18n } from "../../i18n";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { ArrowUpRight, Calendar, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";

function loadCalendlyScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Calendly) return Promise.resolve();
  const existing = document.querySelector(`script[src="${CALENDLY_SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve) => {
      if (window.Calendly) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = CALENDLY_SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });
}

export default function LeadCalendly() {
  const { t, locale } = useI18n();
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    restaurant: "",
    city: "",
    country: "",
    plan_interest: "",
    message: "",
  });
  const calendlyMountRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (mounted) setCalendlyUrl(r.data?.calendly_url || "");
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Initialise Calendly inline widget when the URL + mount node are available
  useEffect(() => {
    if (!calendlyUrl || !calendlyMountRef.current) return;
    let cancelled = false;
    const mount = calendlyMountRef.current;

    loadCalendlyScript()
      .then(() => {
        if (cancelled || !mount || !window.Calendly) return;
        // Clear any previous content (e.g. on locale change)
        mount.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: `${calendlyUrl}?hide_gdpr_banner=1&primary_color=1A362D&text_color=1A2522&background_color=ffffff`,
          parentElement: mount,
          prefill: {},
          utm: {},
        });
      })
      .catch(() => {
        // Silent fail — the "Open calendar" link below is the fallback.
      });

    return () => {
      cancelled = true;
    };
  }, [calendlyUrl, locale]);

  const onChange = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.restaurant) {
      toast.error("Please add your name, email and restaurant.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, locale, source: "landing" });
      setSuccess(true);
      setForm({ name: "", email: "", restaurant: "", city: "", country: "", plan_interest: "", message: "" });
      toast.success(t("lead.success_t"));
    } catch (err) {
      toast.error(t("lead.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="lead" className="section-y" data-testid="lead-section">
      <div className="container-x">
        <div className="max-w-2xl">
          <p className="eyebrow reveal">{t("lead.eyebrow")}</p>
          <h2 className="display-h2 mt-4 text-brand-ink reveal" data-delay="1" data-testid="lead-title">
            {t("lead.title")}
          </h2>
          <p className="body-lg mt-5 reveal" data-delay="2">{t("lead.lead")}</p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Calendly card */}
          <div className="lg:col-span-5 reveal" data-testid="calendly-card">
            <div className="card-surface p-8 md:p-10 h-full flex flex-col">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-stone text-brand-forest">
                  <Calendar size={18} strokeWidth={1.8} />
                </span>
                <h3 className="display-h3 text-brand-ink">{t("lead.calendly_t")}</h3>
              </div>
              <p className="body-lg mt-4">{t("lead.calendly_d")}</p>
              <div className="mt-6 w-full rounded-xl border border-brand-line overflow-hidden bg-white" style={{ minHeight: "700px" }}>
                {calendlyUrl ? (
                  <div
                    ref={calendlyMountRef}
                    data-testid="calendly-inline-widget"
                    style={{ minWidth: "320px", height: "700px" }}
                  />
                ) : (
                  <div className="w-full h-[700px] flex flex-col items-center justify-center text-center px-6">
                    <div className="font-serif text-2xl text-brand-ink">Calendar loading soon</div>
                    <p className="text-sm text-brand-mute mt-2 max-w-xs">
                      A live booking link will appear here once configured. In the meantime, use the form on the right.
                    </p>
                  </div>
                )}
              </div>
              {calendlyUrl && (
                <a
                  href={calendlyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="calendly-open-link"
                  className="btn-ghost mt-6 self-start"
                >
                  {t("lead.open_calendly")} <ArrowUpRight size={16} strokeWidth={1.8} />
                </a>
              )}
            </div>
          </div>

          {/* Form card */}
          <div className="lg:col-span-7 reveal" data-delay="1">
            <form
              onSubmit={onSubmit}
              className="card-surface p-8 md:p-10"
              data-testid="lead-form"
            >
              <h3 className="display-h3 text-brand-ink">{t("lead.form_t")}</h3>

              {success ? (
                <div className="mt-8 flex items-start gap-4 rounded-xl bg-brand-sand p-6" data-testid="lead-success">
                  <CheckCircle2 size={28} strokeWidth={1.5} className="text-brand-forest shrink-0 mt-1" />
                  <div>
                    <p className="font-serif text-2xl text-brand-ink">{t("lead.success_t")}</p>
                    <p className="text-brand-mute mt-2">{t("lead.success_d")}</p>
                  </div>
                </div>
              ) : (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="lf_name" className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_name")}
                    </Label>
                    <Input
                      id="lf_name"
                      data-testid="lead-input-name"
                      value={form.name}
                      onChange={onChange("name")}
                      required
                      className="bg-white border-brand-line focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lf_email" className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_email")}
                    </Label>
                    <Input
                      id="lf_email"
                      type="email"
                      data-testid="lead-input-email"
                      value={form.email}
                      onChange={onChange("email")}
                      required
                      className="bg-white border-brand-line focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="lf_restaurant" className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_restaurant")}
                    </Label>
                    <Input
                      id="lf_restaurant"
                      data-testid="lead-input-restaurant"
                      value={form.restaurant}
                      onChange={onChange("restaurant")}
                      required
                      className="bg-white border-brand-line focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lf_city" className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_city")}
                    </Label>
                    <Input
                      id="lf_city"
                      data-testid="lead-input-city"
                      value={form.city}
                      onChange={onChange("city")}
                      className="bg-white border-brand-line focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lf_country" className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_country")}
                    </Label>
                    <Input
                      id="lf_country"
                      data-testid="lead-input-country"
                      value={form.country}
                      onChange={onChange("country")}
                      className="bg-white border-brand-line focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_plan")}
                    </Label>
                    <Select value={form.plan_interest} onValueChange={onChange("plan_interest")}>
                      <SelectTrigger data-testid="lead-select-plan" className="bg-white border-brand-line">
                        <SelectValue placeholder={t("lead.no_pref")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter" data-testid="lead-plan-starter">Starter — €25/month</SelectItem>
                        <SelectItem value="growth" data-testid="lead-plan-growth">Growth — €125/month</SelectItem>
                        <SelectItem value="pro" data-testid="lead-plan-pro">Pro — €249/month</SelectItem>
                        <SelectItem value="unsure" data-testid="lead-plan-unsure">{t("lead.no_pref")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="lf_message" className="text-xs uppercase tracking-[0.16em] text-brand-mute">
                      {t("lead.f_message")}
                    </Label>
                    <Textarea
                      id="lf_message"
                      data-testid="lead-input-message"
                      rows={4}
                      value={form.message}
                      onChange={onChange("message")}
                      className="bg-white border-brand-line focus-visible:ring-brand-forest"
                    />
                  </div>
                  <div className="sm:col-span-2 flex flex-wrap items-center gap-4 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="lead-form-submit"
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? t("lead.sending") : t("lead.submit")}
                    </button>
                    <span className="text-xs text-brand-mute">
                      GDPR-compliant · No newsletter spam · We reply within one working day.
                    </span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
